import {useCallback, useEffect, useState} from 'react';

import {ApiMetadata} from '@shared/api/model';
import {ApiCallReq, ApiCallRes, ApiDef} from '@shared/api/registry';
import {deepEqual} from '@shared/lib/object_utils';

import {apiCall} from '@shared-frontend/api';
import {notifyError} from '@shared-frontend/lib/notification';

export function useApiCall<
  Api extends ApiMetadata<unknown>,
  Path extends keyof ApiDef<Api>,
  Req extends ApiCallReq<ApiDef<Api>[Path]>,
>(
  api: Api,
  path: Path,
  req: Req,
  opts?: {errMsg?: string}
): {
  data: ApiCallRes<ApiDef<Api>[Path]> | undefined;
  setData: React.Dispatch<React.SetStateAction<ApiCallRes<ApiDef<Api>[Path]> | undefined>>;
  refreshData: () => Promise<void>;
} {
  const [data, setData] = useState<ApiCallRes<ApiDef<Api>[Path]> | undefined>();
  const [cachedReq, setCachedReq] = useState(req);

  useEffect(() => {
    if (cachedReq !== req && !deepEqual(cachedReq, req)) {
      setCachedReq(req);
    }
  }, [cachedReq, req]);

  const refreshData = useCallback(
    async () =>
      apiCall(api, path, cachedReq)
        .then(setData)
        .catch(err =>
          opts?.errMsg !== undefined ? notifyError(err, {message: opts.errMsg}) : notifyError(err)
        ),
    [api, path, cachedReq, opts?.errMsg]
  );

  useEffect(() => {
    refreshData().catch(() => {});
  }, [refreshData]);

  return {data, setData, refreshData};
}
